import {useCallback, useSyncExternalStore} from 'react'
import {AGENT_TASKS_SELECTED_MODEL_KEY} from '../utils/constants'

/**
 * Hook for managing the latest selected agent model as a plain string in localStorage.
 * Uses useSyncExternalStore to properly subscribe to localStorage changes across tabs.
 */
export function useLatestAgentModel(): [string | undefined, (value: string | undefined) => void] {
  const subscribe = useCallback((callback: () => void) => {
    const handleStorageChange = (e: StorageEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === AGENT_TASKS_SELECTED_MODEL_KEY) {
        callback()
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const getSnapshot = useCallback(() => {
    try {
      return localStorage.getItem(AGENT_TASKS_SELECTED_MODEL_KEY) ?? undefined
    } catch {
      return undefined
    }
  }, [])

  const getServerSnapshot = useCallback(() => undefined, [])

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setValue = useCallback((newValue: string | undefined) => {
    try {
      if (newValue === undefined) {
        localStorage.removeItem(AGENT_TASKS_SELECTED_MODEL_KEY)
      } else {
        localStorage.setItem(AGENT_TASKS_SELECTED_MODEL_KEY, newValue)
      }
      // Dispatch a storage event for same-window updates
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: AGENT_TASKS_SELECTED_MODEL_KEY,
          newValue: newValue ?? null,
          oldValue: localStorage.getItem(AGENT_TASKS_SELECTED_MODEL_KEY),
        }),
      )
    } catch {
      // localStorage not available
    }
  }, [])

  return [value, setValue]
}
